import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabContent } from '@/components/ui';
import { PageContainer } from '@/components/layout';
import { useMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { MetricViewHeader, type MetricViewActiveTab } from '@/components/metric-view/MetricViewHeader';
import { MetricViewFilters } from '@/components/metric-view/MetricViewFilters';
import { MetricViewTabs } from '@/components/metric-view/MetricViewTabs';
import { WorkflowHygieneGroup } from '@/components/metric-view/workflow-hygiene/WorkflowHygieneGroup';
import { HygieneDrillTab } from '@/components/metric-view/workflow-hygiene/drill/HygieneDrillTab';
import { EpicCompletionCard } from '@/components/metric-view/EpicCompletionCard';
import { CycleTimeScatterCard } from '@/components/metric-view/CycleTimeScatterCard';
import { ThroughputTrendCard } from '@/components/metric-view/ThroughputTrendCard';
import { WipTrendCard } from '@/components/metric-view/WipTrendCard';
import { CycleTimeP85Card } from '@/components/metric-view/CycleTimeP85Card';
import { ThroughputSummaryCard } from '@/components/metric-view/ThroughputSummaryCard';
import { WipSummaryCard } from '@/components/metric-view/WipSummaryCard';
import { QualityCaveatsBanner } from '@/components/metric-view/QualityCaveatsBanner';
import { EpicBreakdownCard } from '@/components/metric-view/EpicBreakdownCard';
import { EngineeringDoraTab } from '@/components/metric-view/EngineeringDoraTab';
import { CodeQualityTab } from '@/components/metric-view/CodeQualityTab';
import { DevExSurveyContent } from '@/components/shared/DevExSurveyContent';

const VALID_TABS: ReadonlySet<MetricViewActiveTab> = new Set<MetricViewActiveTab>([
  'product-flow',
  'dora',
  'code-quality',
  'devex-survey',
  // Workflow Hygiene drill-down tabs (PRODOPSAI-385). Visible only when
  // active — see MetricViewTabs.
  'hygiene-drill-unlinked',
  'hygiene-drill-statuses',
  'hygiene-drill-types',
]);

const DEFAULT_TAB: MetricViewActiveTab = 'product-flow';

function parseTab(value: string | null): MetricViewActiveTab {
  if (value && VALID_TABS.has(value as MetricViewActiveTab)) {
    return value as MetricViewActiveTab;
  }
  return DEFAULT_TAB;
}

export function MetricViewPage() {
  const {
    filters,
    isCustom,
    scopeOptions,
    pills,
    handleProductChange,
    handleTeamChange,
    handlePillChange,
    handleCustomRangeChange,
    handleIssueTypeChange,
    handleIncludeArchivedChange,
  } = useMetricViewFilters();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTab(searchParams.get('tab'));

  const handleTabChange = useCallback((next: string) => {
    const tab = parseTab(next);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (tab === DEFAULT_TAB) {
        params.delete('tab');
      } else {
        params.set('tab', tab);
      }
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // Closing a drill-down tab returns the user to Product Flow (the
  // default). Removing `?tab` keeps history clean — no dangling entry.
  const handleCloseDrill = useCallback(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('tab');
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // When a specific team is active in the scope filter, the P85 card's
  // "Team" column should show the team name. The backend endpoint groups
  // by product, so we resolve the label client-side from the filter state.
  const selectedTeamName = useMemo(() => {
    if (!filters.teamId) return null;
    return scopeOptions.teams.find((t) => t.id === filters.teamId)?.teamName ?? null;
  }, [filters.teamId, scopeOptions.teams]);

  // PRODOPSAI-678: the review-flow section is scoped to Nordic ERP, so the DORA
  // tab needs the product's name rather than just its id.
  const selectedProductName = useMemo(() => {
    if (!filters.productId) return null;
    return scopeOptions.products.find((p) => p.id === filters.productId)?.productName ?? null;
  }, [filters.productId, scopeOptions.products]);

  // The issue-type row is only meaningful for Product Flow. DORA + Code
  // Quality + DevEx Survey tabs grey it out (kept rendered to avoid layout
  // shift); the underlying ?type= URL param is preserved so flipping back
  // to Product Flow restores the prior filter.
  const issueTypeRowDisabled = activeTab !== 'product-flow';

  return (
    <PageContainer variant="wide" className="space-y-6 p-6">
      <MetricViewHeader activeTab={activeTab} />
      <MetricViewFilters
        productId={filters.productId}
        teamId={filters.teamId}
        pillId={filters.pill.id}
        issueType={filters.issueType}
        includeArchived={filters.includeArchived}
        isCustom={isCustom}
        from={filters.from}
        to={filters.to}
        products={scopeOptions.products}
        teams={scopeOptions.teams}
        pills={pills}
        onProductChange={handleProductChange}
        onTeamChange={handleTeamChange}
        onPillChange={handlePillChange}
        onCustomRangeChange={handleCustomRangeChange}
        onIssueTypeChange={handleIssueTypeChange}
        onIncludeArchivedChange={handleIncludeArchivedChange}
        issueTypeRowDisabled={issueTypeRowDisabled}
      />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <MetricViewTabs activeTab={activeTab} onCloseDrill={handleCloseDrill} />
        <TabContent value="hygiene-drill-unlinked">
          <HygieneDrillTab source="unlinked" filters={filters} />
        </TabContent>
        <TabContent value="hygiene-drill-statuses">
          <HygieneDrillTab source="statuses" filters={filters} />
        </TabContent>
        <TabContent value="hygiene-drill-types">
          <HygieneDrillTab source="types" filters={filters} />
        </TabContent>
        <TabContent value="product-flow" className="space-y-6">
          {/* Workflow Hygiene cards (PRODOPSAI-377) — only render for a
              single-team scope. The wrapper handles the gomning + fetch. */}
          <WorkflowHygieneGroup filters={filters} />
          {/* Column split (PRODOPSAI-664 / PRODOPSAI-666): the middle block holds
              the chart cards (Cycle Time scatter, Throughput / WIP trend), which
              fall back to a non-chart summary/sparkline below 400px rendered width.
              About 368px of horizontal chrome (256px sidebar + lg:p-8 main padding
              + PageContainer p-6) is consumed before this grid, so a 3-column dense
              split cannot give the chart column 400px until the viewport reaches
              ~1536px. The dense split is therefore deferred to 2xl; below 2xl the
              chart block stays full-width (lg:col-span-12, stacked under epic +
              summaries) so the charts are always legible. An earlier xl:col-span-5
              attempt (PRODOPSAI-664) only shrank the dead-band to ~1280–1362px
              rather than removing it. */}
          <div className="mx-auto grid max-w-[960px] grid-cols-1 gap-6 lg:max-w-none lg:grid-cols-12">
            <div className="order-1 lg:col-span-7 2xl:col-span-4">
              <EpicCompletionCard filters={filters} />
            </div>
            <div className="order-3 space-y-6 lg:order-3 lg:col-span-12 2xl:order-2 2xl:col-span-5">
              <CycleTimeScatterCard filters={filters} />
              <ThroughputTrendCard filters={filters} />
              <WipTrendCard filters={filters} />
            </div>
            <div className="order-2 space-y-6 lg:order-2 lg:col-span-5 2xl:order-3 2xl:col-span-3">
              <CycleTimeP85Card filters={filters} selectedTeamName={selectedTeamName} />
              <ThroughputSummaryCard filters={filters} />
              <WipSummaryCard filters={filters} />
            </div>
          </div>
          <QualityCaveatsBanner />
          <EpicBreakdownCard filters={filters} />
        </TabContent>
        <TabContent value="dora">
          <EngineeringDoraTab
            filters={filters}
            productName={selectedProductName}
            selectedTeamName={selectedTeamName}
          />
        </TabContent>
        <TabContent value="code-quality">
          <CodeQualityTab filters={filters} />
        </TabContent>
        <TabContent value="devex-survey">
          <DevExSurveyContent scope={{
            productId: filters.productId,
            teamId: filters.teamId,
          }} />
        </TabContent>
      </Tabs>
    </PageContainer>
  );
}
